import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { RecentEntry } from '../types/command-palette';

/**
 * How many entries we keep per workspace. The palette only surfaces the top few inline, but we
 * retain a deeper tail so the "Browse all recent" drill-in and its search have something to
 * work with. Bounded so localStorage can't grow without limit on a long-lived session.
 */
const MAX_ENTRIES_PER_WORKSPACE = 25;

/**
 * Bump when the persisted shape changes and add a branch to migrate() below. Without this,
 * a user with old entries in localStorage gets rows the current renderer can't handle.
 */
const STORE_VERSION = 1;

interface RecentPagesState {
  /** Keyed by workspaceId, newest-first within each list. */
  byWorkspace: Record<string, RecentEntry[]>;
  record: (workspaceId: string, entry: RecentEntry) => void;
  remove: (workspaceId: string, key: string) => void;
  clear: (workspaceId: string) => void;
}

/**
 * Storage is namespaced per app (`inbox-clinic:` / historical `anchor-portal:`) rather than shared, so the
 * two portals never read each other's history even when served from the same origin during
 * local dev. The key is only known once an app boots, so the store is created with
 * `skipHydration` and does not read localStorage until configureRecentPages() supplies it —
 * same injectable-configuration pattern as setAuthTokenGetter()/setAuthStateSubscriber().
 */
let configuredAppKey: string | null = null;

export const useRecentPagesStore = create<RecentPagesState>()(
  persist(
    (set) => ({
      byWorkspace: {},

      record: (workspaceId, entry) =>
        set((state) => {
          const current = state.byWorkspace[workspaceId] ?? [];

          // Drop any existing row for this entity before unshifting, so a revisit moves the
          // entry to the top and refreshes its label/avatar instead of leaving a stale copy
          // further down the list.
          const deduped = current.filter((e) => e.key !== entry.key);

          return {
            byWorkspace: {
              ...state.byWorkspace,
              [workspaceId]: [entry, ...deduped].slice(0, MAX_ENTRIES_PER_WORKSPACE),
            },
          };
        }),

      remove: (workspaceId, key) =>
        set((state) => {
          const current = state.byWorkspace[workspaceId];
          if (!current) return state;
          return {
            byWorkspace: {
              ...state.byWorkspace,
              [workspaceId]: current.filter((e) => e.key !== key),
            },
          };
        }),

      clear: (workspaceId) =>
        set((state) => {
          const { [workspaceId]: _dropped, ...rest } = state.byWorkspace;
          return { byWorkspace: rest };
        }),
    }),
    {
      // Placeholder only — the real, app-namespaced name is installed by
      // configureRecentPages() before any read or write happens.
      name: 'unconfigured:recent-pages',
      version: STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      skipHydration: true,
      partialize: (state) => ({ byWorkspace: state.byWorkspace }),
      migrate: (persisted, fromVersion) => {
        // v1 is the first shipped shape; anything older than the current version is discarded
        // rather than guessed at. Replace this with real reshaping when v2 lands.
        if (fromVersion !== STORE_VERSION) {
          return { byWorkspace: {} };
        }
        return persisted as { byWorkspace: Record<string, RecentEntry[]> };
      },
    }
  )
);

/**
 * Called once per app at startup (from `main.tsx`) with that app's storage namespace, e.g.
 * `inbox-clinic` or historical `anchor-portal`. Installs the real storage key and performs the initial
 * hydration that `skipHydration` deferred.
 *
 * Safe to call more than once with the same key (HMR re-runs module side effects); calling it
 * with a *different* key re-points the store at that namespace and re-hydrates from it.
 */
export function configureRecentPages(appKey: string) {
  if (configuredAppKey === appKey) return;
  configuredAppKey = appKey;
  useRecentPagesStore.persist.setOptions({ name: `${appKey}:recent-pages` });
  void useRecentPagesStore.persist.rehydrate();
}

/** Exposed for tests and for surfacing the active namespace in debug output. */
export function getRecentPagesAppKey() {
  return configuredAppKey;
}
